import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowRight, Phone, MapPin, Clock, CalendarCheck, Mail, MessageCircle,
  Building2, Send, CheckCircle2, Paperclip, X
} from "lucide-react";
import { formatHours, track } from "@/components/landing/shared";
import { getImageUrl } from "@/utils/imageUrl";

const API = process.env.REACT_APP_BACKEND_URL;

const REASONS = [
  "Informacion general",
  "Taller y postventa",
  "Recambios",
  "Chapa y pintura",
  "Vehículos usados y Km0",
  "Promociones",
  "Otra consulta",
];

export default function ContactPage() {
  const [content, setContent] = useState(null);
  const [config, setConfig] = useState(null);
  const [centers, setCenters] = useState([]);
  const [generalContact, setGeneralContact] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", center: "", reason: "", message: "", privacy: false, commercial: false });
  const [attachment, setAttachment] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/site-content`).then(r => r.data).catch(() => null),
      axios.get(`${API}/api/config`).then(r => r.data).catch(() => null),
      axios.get(`${API}/api/centers?active_only=true`).then(r => r.data).catch(() => []),
      axios.get(`${API}/api/public/general-contact`).then(r => r.data).catch(() => null),
    ]).then(([cnt, cfg, ctr, gc]) => { setContent(cnt); setConfig(cfg); setCenters(ctr); setGeneralContact(gc); });
  }, []);

  if (!content || !config) return <div className="flex items-center justify-center min-h-screen bg-white"><div className="animate-spin h-8 w-8 border-2 border-slate-900 border-t-transparent rounded-full" /></div>;

  const cn = content.contacto || {};
  const hero = cn.hero || {};
  const formCfg = cn.form || {};
  const channels = cn.channels || {};
  const centrosSec = cn.centros || {};
  const ctaFinal = cn.cta_final || {};
  const seo = cn.seo || {};
  const companyName = config.company_name || "ASCAUTO";
  const phone = config.phone || "";
  const waPhone = generalContact?.whatsapp_phone_clean || "";
  const waMessage = generalContact?.whatsapp_message || "";

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("El archivo supera 10MB"); return; }
    setAttachment(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Nombre y email son obligatorios"); return; }
    if (!form.privacy) { toast.error("Debes aceptar la politica de privacidad"); return; }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("phone", form.phone);
      fd.append("email", form.email);
      fd.append("center", form.center);
      fd.append("reason", form.reason);
      fd.append("message", form.message);
      fd.append("privacy_accepted", "true");
      fd.append("commercial_accepted", form.commercial ? "true" : "false");
      if (attachment) fd.append("attachment", attachment);
      await axios.post(`${API}/api/contact/submit`, fd);
      setSent(true);
      track("contact_form_submit", form.reason);
      toast.success("Consulta enviada correctamente");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar la consulta");
    }
    setSending(false);
  };

  return (
    <div className="bg-white" data-testid="contact-page">
      <title>{seo.title || `Contacto | ${companyName}`}</title>
      <meta name="description" content={seo.description || `Contacta con ${companyName}. Estamos aqui para ayudarte.`} />

      {/* HERO */}
      <section className="relative min-h-[50vh] flex items-center overflow-hidden" data-testid="contact-hero">
        <div className="absolute inset-0">
          {hero.image_url ? (
            <img src={getImageUrl(hero.image_url)} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-slate-900" />
          )}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900/85 to-slate-900/70" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 w-full">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px flex-1 max-w-[40px] bg-red-600" />
              <span className="text-red-400 text-[11px] font-semibold uppercase tracking-[0.3em]">{hero.label || "Contacto"}</span>
            </div>
            <h1 className="heading-industrial text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[0.95] mb-6" data-testid="contact-h1">
              {hero.title || "Contacto"}
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl mb-8">
              {hero.subtitle || `Estamos aquí para ayudarte desde Móstoles y Madrid.`}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              {phone && (
                <a href={`tel:${phone.replace(/\s/g, "")}`} onClick={() => track("contact_hero_call", phone)}>
                  <Button className="bg-red-600 hover:bg-red-700 text-white rounded-none text-sm uppercase tracking-wider font-bold h-12 px-8">
                    <Phone className="w-4 h-4 mr-2" /> {hero.cta_primary || "Llamar ahora"}
                  </Button>
                </a>
              )}
              <Link to="/cita">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-none text-sm uppercase tracking-wider font-bold h-12 px-8">
                  <CalendarCheck className="w-4 h-4 mr-2" /> {hero.cta_secondary || "Reservar cita"}
                </Button>
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent z-10" />
      </section>

      {/* FORM */}
      <section className="py-20 lg:py-28" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
            <div className="lg:col-span-3">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-1 bg-red-600" />
                <span className="text-red-600 text-[11px] font-semibold uppercase tracking-[0.25em]">{formCfg.label || "Formulario"}</span>
              </div>
              <h2 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 uppercase mb-3">
                {formCfg.title || "Envianos tu consulta"}
              </h2>
              <p className="text-sm text-slate-500 mb-8">{formCfg.subtitle || "Cuentanos que necesitas y te responderemos lo antes posible."}</p>

              {sent ? (
                <div className="bg-green-50 border border-green-200 p-8 text-center" data-testid="contact-success">
                  <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <h3 className="heading-industrial text-xl font-bold text-slate-900 uppercase mb-2">Consulta enviada</h3>
                  <p className="text-sm text-slate-600">{formCfg.success_text || "Hemos recibido tu consulta. Te responderemos lo antes posible."}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Nombre y apellidos *</Label>
                      <Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} required className="rounded-none border-slate-300 h-11" data-testid="contact-name" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Telefono</Label>
                      <Input value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))} className="rounded-none border-slate-300 h-11" data-testid="contact-phone" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Email *</Label>
                    <Input type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} required className="rounded-none border-slate-300 h-11" data-testid="contact-email" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Centro de interes</Label>
                      <Select value={form.center} onValueChange={v => setForm(p => ({...p, center: v}))}>
                        <SelectTrigger className="rounded-none border-slate-300 h-11" data-testid="contact-center"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                        <SelectContent>
                          {centers.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                          <SelectItem value="Indiferente">Indiferente</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Motivo de contacto</Label>
                      <Select value={form.reason} onValueChange={v => setForm(p => ({...p, reason: v}))}>
                        <SelectTrigger className="rounded-none border-slate-300 h-11" data-testid="contact-reason"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                        <SelectContent>
                          {REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Mensaje</Label>
                    <Textarea value={form.message} onChange={e => setForm(p => ({...p, message: e.target.value}))} rows={5} className="rounded-none border-slate-300" data-testid="contact-message" />
                  </div>
                  {/* Attachment */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Adjunto (opcional)</Label>
                    {attachment ? (
                      <div className="flex items-center gap-2 p-3 border border-slate-300 bg-slate-50">
                        <Paperclip className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-700 flex-1 truncate">{attachment.name}</span>
                        <button type="button" onClick={() => { setAttachment(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-slate-300 p-4 text-center hover:border-slate-400 transition-colors cursor-pointer" onClick={() => fileRef.current?.click()}>
                        <Paperclip className="w-5 h-5 text-slate-400 mx-auto mb-1" />
                        <span className="text-xs text-slate-500">Haz clic para adjuntar un archivo (max 10MB)</span>
                      </div>
                    )}
                    <input ref={fileRef} type="file" className="hidden" onChange={handleFile} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp" data-testid="contact-file" />
                  </div>
                  {/* Privacy */}
                  <div className="space-y-3 pt-2">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" checked={form.privacy} onChange={e => setForm(p => ({...p, privacy: e.target.checked}))} className="mt-1 accent-red-600" data-testid="contact-privacy" />
                      <span className="text-xs text-slate-600">He leido y acepto la <Link to="/politica-de-privacidad" className="text-red-600 underline" target="_blank">politica de privacidad</Link> *</span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" checked={form.commercial} onChange={e => setForm(p => ({...p, commercial: e.target.checked}))} className="mt-1 accent-red-600" data-testid="contact-commercial" />
                      <span className="text-xs text-slate-600">Acepto recibir comunicaciones comerciales</span>
                    </label>
                  </div>
                  <Button type="submit" disabled={sending} className="bg-red-600 hover:bg-red-700 text-white rounded-none text-sm uppercase tracking-wider font-bold h-12 px-10" data-testid="contact-submit">
                    {sending ? "Enviando..." : <><Send className="w-4 h-4 mr-2" /> Enviar consulta</>}
                  </Button>
                </form>
              )}
            </div>

            {/* CHANNELS */}
            <div className="lg:col-span-2" data-testid="contact-channels">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-1 bg-red-600" />
                <span className="text-red-600 text-[11px] font-semibold uppercase tracking-[0.25em]">{channels.label || "Contacto directo"}</span>
              </div>
              <h3 className="heading-industrial text-xl font-bold text-slate-900 uppercase mb-3">
                {channels.title || "Tambien puedes contactar directamente"}
              </h3>
              <p className="text-sm text-slate-500 mb-8">{channels.subtitle || "Elige la forma que te resulte mas comoda para hablar con nuestro equipo."}</p>
              <div className="space-y-4">
                {phone && (
                  <a href={`tel:${phone.replace(/\s/g, "")}`} className="flex items-center gap-4 p-5 border border-slate-200 hover:border-red-200 hover:shadow-md transition-all group" data-testid="channel-phone">
                    <div className="w-12 h-12 bg-red-50 group-hover:bg-red-600 flex items-center justify-center transition-colors"><Phone className="w-5 h-5 text-red-600 group-hover:text-white transition-colors" /></div>
                    <div><p className="text-sm font-bold text-slate-900">Telefono</p><p className="text-xs text-slate-500">{phone}</p></div>
                  </a>
                )}
                {waPhone && (
                  <a href={`https://wa.me/${waPhone}${waMessage ? `?text=${encodeURIComponent(waMessage)}` : ""}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-5 border border-slate-200 hover:border-green-200 hover:shadow-md transition-all group" data-testid="channel-whatsapp">
                    <div className="w-12 h-12 bg-green-50 group-hover:bg-green-600 flex items-center justify-center transition-colors"><MessageCircle className="w-5 h-5 text-green-600 group-hover:text-white transition-colors" /></div>
                    <div><p className="text-sm font-bold text-slate-900">WhatsApp</p><p className="text-xs text-slate-500">Escribe por WhatsApp</p></div>
                  </a>
                )}
                {config.email && (
                  <a href={`mailto:${config.email}`} className="flex items-center gap-4 p-5 border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all group" data-testid="channel-email">
                    <div className="w-12 h-12 bg-slate-100 group-hover:bg-slate-900 flex items-center justify-center transition-colors"><Mail className="w-5 h-5 text-slate-600 group-hover:text-white transition-colors" /></div>
                    <div><p className="text-sm font-bold text-slate-900">Email</p><p className="text-xs text-slate-500">{config.email}</p></div>
                  </a>
                )}
                <Link to="/cita" className="flex items-center gap-4 p-5 border border-slate-200 hover:border-red-200 hover:shadow-md transition-all group" data-testid="channel-cita">
                  <div className="w-12 h-12 bg-red-50 group-hover:bg-red-600 flex items-center justify-center transition-colors"><CalendarCheck className="w-5 h-5 text-red-600 group-hover:text-white transition-colors" /></div>
                  <div><p className="text-sm font-bold text-slate-900">Reservar cita</p><p className="text-xs text-slate-500">Pide cita online en tu centro</p></div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CENTERS */}
      {centers.length > 0 && (
        <section className="py-20 lg:py-28 bg-slate-50" data-testid="contact-centers">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-red-600 text-[11px] font-semibold uppercase tracking-[0.25em]">{centrosSec.label || "Donde estamos"}</span>
              <h2 className="heading-industrial text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 uppercase mt-3">{centrosSec.title || "Nuestros centros"}</h2>
              {centrosSec.subtitle && <p className="text-sm text-slate-500 mt-3 max-w-xl mx-auto">{centrosSec.subtitle}</p>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {centers.map((c, i) => (
                <div key={c.id} className="bg-white border border-slate-200 overflow-hidden hover:shadow-lg transition-all group" data-testid={`contact-center-${i}`}>
                  <div className="h-48 overflow-hidden relative bg-slate-200">
                    {(c.image_url || c.map_image_url) ? <img src={getImageUrl(c.image_url || c.map_image_url)} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /> : <div className="w-full h-full bg-slate-900 flex items-center justify-center"><Building2 className="w-16 h-16 text-slate-700" /></div>}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/30 to-transparent" />
                    <div className="absolute bottom-4 left-5"><h3 className="heading-industrial text-xl font-bold text-white uppercase">{c.name}</h3></div>
                  </div>
                  <div className="p-6 space-y-3">
                    <div className="flex items-start gap-3"><MapPin className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" /><div><p className="text-sm font-medium text-slate-900">{c.address}</p><p className="text-xs text-slate-500">{c.zip_code} {c.city}</p></div></div>
                    {c.phone && <div className="flex items-center gap-3"><Phone className="w-4 h-4 text-red-600 flex-shrink-0" /><a href={`tel:${c.phone.replace(/\s/g,"")}`} className="text-sm text-slate-700 hover:text-red-600 font-medium">{c.phone}</a></div>}
                    <div className="flex items-center gap-3"><Clock className="w-4 h-4 text-red-600 flex-shrink-0" /><p className="text-sm text-slate-600">{formatHours(c.working_hours)}</p></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA FINAL */}
      <section className="py-24 lg:py-32 bg-slate-900 relative overflow-hidden" data-testid="contact-cta-final">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "repeating-linear-gradient(90deg, rgba(255,255,255,.07) 0 1px, transparent 1px 60px)" }} />
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-red-600 flex items-center justify-center mx-auto mb-8"><Mail className="w-8 h-8 text-white" /></div>
          <h2 className="heading-industrial text-2xl sm:text-3xl lg:text-4xl font-bold text-white uppercase mb-5">{ctaFinal.title || "Estamos para ayudarte"}</h2>
          <p className="text-slate-400 mb-12 max-w-lg mx-auto text-sm sm:text-base leading-relaxed">{ctaFinal.subtitle || "Ya sea una consulta general, una necesidad de taller o una pregunta sobre vehiculos, en Ascauto te responderemos con claridad y rapidez."}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/cita"><Button className="bg-red-600 hover:bg-red-700 text-white rounded-none text-sm uppercase tracking-wider font-bold h-14 px-12 shadow-lg shadow-red-600/30" data-testid="contact-cta-btn">{ctaFinal.button_text || "Reservar Cita"} <ArrowRight className="w-5 h-5 ml-2" /></Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
